/**
 * Core financial calculations for HP Repayment Schedule
 * Uses Effective Interest Method with high precision
 */

/**
 * Calculate interest rate using numpy_financial.rate() algorithm
 * Implements the exact algorithm used by Python's numpy_financial.rate()
 * 
 * @param {number} nper - Number of periods (tenor)
 * @param {number} pmt - Payment amount (must be negative)
 * @param {number} pv - Present value (amount financed, positive)
 * @param {number} fv - Future value (default 0)
 * @param {boolean} whenBegin - True for "In Advance", false for "In Arrears"
 * @param {number} guess - Initial guess for rate (default 0.1)
 * @param {boolean} returnSteps - If true, returns object with rate and calculation steps
 * @returns {number|Object} Interest rate per period, or object with rate and steps if returnSteps=true
 */
export function calculateInterestRate(nper, pmt, pv, fv = 0, whenBegin = false, guess = 0.1, returnSteps = false) {
  const steps = [];
  
  // Add initial information
  steps.push({
    step: 'Input Parameters (NumPy Financial Algorithm)',
    details: {
      'Number of Periods (N)': nper,
      'Payment Amount (PMT)': pmt,
      'Present Value (PV)': pv,
      'Future Value (FV)': fv,
      'Payment Timing': whenBegin ? 'In Advance (begin)' : 'In Arrears (end)',
      'Initial Guess': guess,
      'Tolerance': '1e-6',
      'Max Iterations': 100
    }
  });
  
  const TOLERANCE = 1e-6; // numpy_financial default tolerance
  const MAX_ITERATIONS = 100; // numpy_financial default maxiter
  
  // Convert whenBegin to numpy_financial format (0 for end, 1 for begin)
  const when = whenBegin ? 1 : 0;
  
  // numpy_financial.rate() algorithm
  // Solves: fv + pv*(1+rate)**nper + pmt*(1+rate*when)/rate * ((1+rate)**nper - 1) = 0
  
  let rate = guess;
  
  // Function to calculate the numpy_financial rate equation
  const calculateNumpyError = (r, includeSteps = false) => {
    if (Math.abs(r) < 1e-10) {
      // For very small rates, use linear approximation
      const linearPV = pv * (1 + r * nper);
      const linearPMT = pmt * nper * (1 + r * when);
      const error = fv + linearPV + linearPMT;
      
      if (includeSteps) {
        return {
          error,
          details: {
            'Rate (r)': r,
            'Linear Approximation Used': true,
            'PV term': linearPV,
            'PMT term': linearPMT,
            'FV': fv,
            'Error': error
          }
        };
      }
      return error;
    }
    
    const onePlusR = 1 + r;
    const onePlusRN = Math.pow(onePlusR, nper);
    
    // numpy_financial formula:
    // fv + pv*(1+rate)**nper + pmt*(1+rate*when)/rate * ((1+rate)**nper - 1) = 0
    const pvTerm = pv * onePlusRN;
    const pmtFactor = (1 + r * when) / r;
    const pmtTerm = pmt * pmtFactor * (onePlusRN - 1);
    const error = fv + pvTerm + pmtTerm;
    
    if (includeSteps) {
      return {
        error,
        details: {
          'Rate (r)': r,
          '(1+r)': onePlusR,
          '(1+r)^n': onePlusRN,
          'PV term (pv*(1+r)^n)': pvTerm,
          'PMT factor ((1+r*when)/r)': pmtFactor,
          'PMT term': pmtTerm,
          'FV': fv,
          'Error (fv + pvTerm + pmtTerm)': error
        }
      };
    }
    return error;
  };
  
  // Derivative of the numpy_financial rate equation
  const calculateNumpyDerivative = (r) => {
    if (Math.abs(r) < 1e-10) {
      // Linear approximation for very small rates
      const dPV = pv * nper;
      const dPMT = pmt * nper * when;
      return dPV + dPMT;
    }
    
    const onePlusR = 1 + r;
    const onePlusRN = Math.pow(onePlusR, nper);
    const onePlusRNMinus1 = onePlusRN - 1;
    
    // Derivative of: fv + pv*(1+r)^n + pmt*(1+r*when)/r * ((1+r)^n - 1)
    // d/dr of pv*(1+r)^n = pv * n * (1+r)^(n-1)
    const dPVTerm = pv * nper * Math.pow(onePlusR, nper - 1);
    
    // d/dr of pmt*(1+r*when)/r * ((1+r)^n - 1)
    // Using product rule and quotient rule
    const pmtFactor = (1 + r * when) / r;
    const dPmtFactor = (when * r - (1 + r * when)) / (r * r); // derivative of (1+r*when)/r
    const dOnePlusRNMinus1 = nper * Math.pow(onePlusR, nper - 1); // derivative of (1+r)^n - 1
    
    const dPMTTerm = pmt * (dPmtFactor * onePlusRNMinus1 + pmtFactor * dOnePlusRNMinus1);
    
    return dPVTerm + dPMTTerm;
  };
  
  // Newton-Raphson iteration (matching numpy_financial implementation)
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const errorResult = calculateNumpyError(rate, returnSteps);
    const error = returnSteps ? errorResult.error : errorResult;
    const derivative = calculateNumpyDerivative(rate);
    
    if (returnSteps && i < 5) {
      // Store first few iterations for display
      const errorDetails = returnSteps ? errorResult.details : {};
      steps.push({
        step: `Iteration ${i + 1} (Newton-Raphson)`,
        details: {
          'Current Rate (r)': rate,
          'Error': error,
          'Derivative': derivative,
          ...errorDetails
        }
      });
    }
    
    if (Math.abs(error) < TOLERANCE) {
      // Validate rate is reasonable
      if (rate >= -1 && rate <= 1) {
        if (returnSteps) {
          steps.push({
            step: 'Final Result',
            details: {
              'Calculated Rate (r)': rate,
              'Rate as Percentage': (rate * 100).toFixed(6) + '%',
              'Iterations': i + 1,
              'Final Error': error,
              'Algorithm': 'NumPy Financial rate()'
            }
          });
          return { rate, steps };
        }
        return rate;
      } else {
        // Rate out of bounds, try binary search
        break;
      }
    }
    
    if (Math.abs(derivative) < 1e-10) {
      // Derivative too small, adjust guess
      rate = rate * 1.1;
      if (Math.abs(rate) > 1) {
        // Try binary search as fallback
        break;
      }
      continue;
    }
    
    const newRate = rate - error / derivative;
    
    // Prevent rates that are too extreme
    if (newRate < -0.99 || newRate > 1) {
      // Try binary search as fallback
      break;
    }
    
    if (Math.abs(newRate - rate) < TOLERANCE) {
      if (returnSteps) {
        steps.push({
          step: 'Final Result',
          details: {
            'Calculated Rate (r)': newRate,
            'Rate as Percentage': (newRate * 100).toFixed(6) + '%',
            'Iterations': i + 1,
            'Final Error': error,
            'Algorithm': 'NumPy Financial rate()'
          }
        });
        return { rate: newRate, steps };
      }
      return newRate;
    }
    
    rate = newRate;
  }
  
  // Fallback to binary search with reasonable bounds (matching numpy_financial behavior)
  const binaryResult = binarySearchRate(nper, pmt, pv, fv, whenBegin, returnSteps);
  if (returnSteps) {
    if (binaryResult.steps) {
      steps.push(...binaryResult.steps);
    }
    steps.push({
      step: 'Final Result (Binary Search Fallback)',
      details: {
        'Calculated Rate (r)': binaryResult.rate,
        'Rate as Percentage': (binaryResult.rate * 100).toFixed(6) + '%',
        'Algorithm': 'NumPy Financial rate() with binary search'
      }
    });
    return { rate: binaryResult.rate, steps };
  }
  return binaryResult;
}

/**
 * Binary search fallback for interest rate calculation
 */
function binarySearchRate(nper, pmt, pv, fv, whenBegin, returnSteps = false) {
  // Use reasonable bounds for interest rates (-99% to 100% per period, matching numpy_financial)
  let low = -0.99;
  let high = 1;
  const TOLERANCE = 1e-6;
  const steps = returnSteps ? [] : null;
  const when = whenBegin ? 1 : 0;
  
  // Use numpy_financial formula for binary search
  const calculateError = (r) => {
    if (Math.abs(r) < 1e-10) {
      const linearPV = pv * (1 + r * nper);
      const linearPMT = pmt * nper * (1 + r * when);
      return fv + linearPV + linearPMT;
    }
    const onePlusR = 1 + r;
    const onePlusRN = Math.pow(onePlusR, nper);
    const pvTerm = pv * onePlusRN;
    const pmtFactor = (1 + r * when) / r;
    const pmtTerm = pmt * pmtFactor * (onePlusRN - 1);
    return fv + pvTerm + pmtTerm;
  };
  
  for (let i = 0; i < 100; i++) {
    const mid = (low + high) / 2;
    const errorResult = calculateError(mid, returnSteps);
    const error = returnSteps ? errorResult.error : errorResult;
    
    if (returnSteps && i < 3) {
      // Store first few iterations for display
      steps.push({
        step: `Binary Search Iteration ${i + 1}`,
        details: {
          'Low Bound': low,
          'High Bound': high,
          'Mid Point (r)': mid,
          'Error': error
        }
      });
    }
    
    if (Math.abs(error) < TOLERANCE) {
      // Validate rate is reasonable
      if (mid >= 0 && mid <= 1) {
        if (returnSteps) {
          return { rate: mid, steps };
        }
        return mid;
      }
    }
    
    if (error > 0) {
      low = mid;
    } else {
      high = mid;
    }
    
    if (high - low < TOLERANCE) {
      const finalRate = (low + high) / 2;
      if (finalRate >= 0 && finalRate <= 1) {
        if (returnSteps) {
          return { rate: finalRate, steps };
        }
        return finalRate;
      } else {
        throw new Error('Unable to calculate valid interest rate. Please check your inputs.');
      }
    }
  }
  
  const finalRate = (low + high) / 2;
  if (finalRate >= 0 && finalRate <= 1) {
    if (returnSteps) {
      return { rate: finalRate, steps };
    }
    return finalRate;
  } else {
    throw new Error('Unable to calculate valid interest rate. Please check your inputs.');
  }
}

/**
 * Generate HP Repayment Schedule using Effective Interest Method
 * 
 * @param {Object} params - Input parameters
 * @param {number} params.assetCost - Total asset cost (excluding interest)
 * @param {number} params.deposit - Deposit paid
 * @param {number} params.tenor - Number of instalments (N)
 * @param {number} params.instalmentAmount - Instalment amount (PMT, will be stored as negative)
 * @param {Date} params.firstInstalmentDate - Date of first instalment
 * @param {boolean} params.inAdvance - True if payment "In Advance", false if "In Arrears"
 * @returns {Array} Array of schedule rows
 */
export function generateHPSchedule({
  assetCost,
  deposit = 0,
  tenor,
  instalmentAmount,
  firstInstalmentDate,
  inAdvance
}) {
  const pv = assetCost - (deposit || 0); // Amount financed (positive)
  const pmt = -Math.abs(instalmentAmount); // Payment must be negative
  
  // Validate inputs
  if (pv <= 0) {
    throw new Error('Amount financed (Asset Cost - Deposit) must be positive');
  }
  if (Math.abs(pmt) * tenor < pv) {
    throw new Error('Total instalments must be greater than or equal to amount financed');
  }
  
  // Calculate interest rate with step-by-step details
  let rateResult;
  try {
    rateResult = calculateInterestRate(tenor, pmt, pv, 0, inAdvance, 0.1, true);
    const rate = typeof rateResult === 'object' ? rateResult.rate : rateResult;
    
    // Validate rate is reasonable (between 0 and 1 for monthly rates)
    if (isNaN(rate) || !isFinite(rate) || rate < 0 || rate > 1) {
      throw new Error(`Invalid interest rate calculated: ${rate}. Please check your inputs.`);
    }
  } catch (error) {
    throw new Error(`Failed to calculate interest rate: ${error.message}. Please verify that instalment amount and tenor are correct.`);
  }
  
  const rate = typeof rateResult === 'object' ? rateResult.rate : rateResult;
  const rateCalculationSteps = typeof rateResult === 'object' ? rateResult.steps : null;
  
  const schedule = [];
  
  // Instalment 0: Deposit (only if deposit > 0)
  if (deposit && deposit > 0) {
    const depositDate = inAdvance 
      ? new Date(firstInstalmentDate) 
      : new Date(firstInstalmentDate);
    
    // For "In Arrears", deposit is one period before first instalment
    if (!inAdvance) {
      depositDate.setMonth(depositDate.getMonth() - 1);
    }
    
    schedule.push({
      instalmentNo: 0,
      dueDate: new Date(depositDate),
      openingBalance: assetCost,
      principal: deposit,
      interest: 0,
      instalment: deposit,
      closingBalance: pv
    });
  }
  
  // Instalments 1 to N
  let openingBalance = pv;
  
  for (let i = 1; i <= tenor; i++) {
    const dueDate = new Date(firstInstalmentDate);
    dueDate.setMonth(dueDate.getMonth() + (i - 1));
    
    let interest, principal, closingBalance;
    
    // For "In Advance" payments, Instalment 1 has no interest (payment at start of period)
    if (inAdvance && i === 1) {
      interest = 0;
      principal = Math.abs(pmt);
      closingBalance = openingBalance - principal;
    } else {
      // For "In Arrears" or subsequent "In Advance" instalments, calculate interest normally
      interest = openingBalance * rate;
      principal = Math.abs(pmt) - interest;
      closingBalance = openingBalance - principal;
    }
    
    // Validate calculations don't produce invalid values
    if (!isFinite(interest) || !isFinite(principal) || !isFinite(closingBalance)) {
      throw new Error(`Invalid calculation at instalment ${i}. Interest rate may be too high.`);
    }
    
    schedule.push({
      instalmentNo: i,
      dueDate: new Date(dueDate),
      openingBalance: openingBalance,
      principal: principal,
      interest: interest,
      instalment: Math.abs(pmt),
      closingBalance: closingBalance
    });
    
    openingBalance = closingBalance;
  }
  
  // Attach rate calculation steps to schedule for display
  schedule.rateCalculationSteps = rateCalculationSteps;
  schedule.calculatedRate = rate;
  schedule.inAdvance = inAdvance; // Store payment timing for Step 2 YA grouping
  
  return schedule;
}

